import { CALL_API } from 'redux-api-middleware';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { NMS_GATEWAY_PATH } from '../../../../config/endpoints';

export const normaliseUKPhoneNumber = value => {
  if (typeof value !== 'string' || !/^\+?[\d\s()-]+$/.test(value.trim())) return null;

  const number = value.replace(/[\s()-]/g, '');
  if (!/^(?:0[1-9]|\+?44[1-9])\d+$/.test(number)) return null;

  const international = number.startsWith('44') ? `+${number}` : number;
  const phone = parsePhoneNumberFromString(international, { defaultCountry: 'GB', extract: false });
  return phone && phone.country === 'GB' && phone.isValid() ? number : null;
};

export const ACCESS_REQUEST = 'NMS/ACCESS_REQUEST';
export const ACCESS_RECEIVE = 'NMS/ACCESS_RECEIVE';
export const ACCESS_FAILURE = 'NMS/ACCESS_FAILURE';
export const CONSENT_REQUEST = 'NMS/CONSENT_REQUEST';
export const CONSENT_RECEIVE = 'NMS/CONSENT_RECEIVE';
export const CONSENT_FAILURE = 'NMS/CONSENT_FAILURE';

const makeTypes = (types, meta) => types.map(type => ({ type, meta }));
let nextAccessRequestId = 0;

export const fetchAccess = (token, endpoint = NMS_GATEWAY_PATH) => {
  nextAccessRequestId += 1;
  return {
    [CALL_API]: {
      endpoint: `${endpoint}/access/${encodeURIComponent(token)}`,
      method: 'GET',
      options: { cache: 'no-store' },
      types: makeTypes([ACCESS_REQUEST, ACCESS_RECEIVE, ACCESS_FAILURE], {
        token, requestId: nextAccessRequestId,
      }),
    },
  };
};

export const submitConsent = (token, consentStatus, contactNumber, endpoint = NMS_GATEWAY_PATH) => ({
  [CALL_API]: {
    endpoint: `${endpoint}/consent/${encodeURIComponent(token)}`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(consentStatus === 'provided'
      ? { consentStatus, contactNumber }
      : { consentStatus }),
    types: [
      ...makeTypes([CONSENT_REQUEST], { token, consentStatus }),
      { type: CONSENT_RECEIVE, meta: { token, consentStatus }, payload: () => null },
      ...makeTypes([CONSENT_FAILURE], { token, consentStatus }),
    ],
  },
});

const errorOutcomes = {
  NMS_LINK_EXPIRED: 'expired',
  NMS_ALREADY_RESPONDED: 'already-responded',
};

export const getFailureOutcome = (payload = {}) => {
  if (!payload) return null;
  const body = payload.response || {};
  const code = (body.error && (body.error.errorCode || body.error.code)) || body.code;
  if (errorOutcomes[code]) return errorOutcomes[code];
  if (payload.status === 410) return 'expired';
  if (payload.status === 409) return 'already-responded';
  if (payload.status === 404) return 'unavailable';
  if (payload.status === 401) return 'unavailable';
  return null;
};

const loadError = 'We couldn’t check your link right now. Please try again.';
const saveError = 'We couldn’t confirm whether your response was saved. Please check your link again before sending another response.';

export const initialState = {
  token: null,
  requestId: null,
  status: 'idle',
  medicines: [],
  submitting: false,
  pendingConsent: null,
  error: '',
};

const getMedicines = medicines => (Array.isArray(medicines) ? medicines : [])
  .map((medicine, index) => {
    const name = typeof medicine === 'string' ? medicine : medicine && medicine.name;
    if (typeof name !== 'string' || !name.trim()) return null;
    return {
      name: name.trim(),
      form: typeof medicine.form === 'string' ? medicine.form : 'capsule',
      prodDesc: '',
      vmpId: String(index),
    };
  }).filter(Boolean);

export default (state = initialState, action = {}) => {
  const meta = action.meta || {};
  if (action.type !== ACCESS_REQUEST && meta.token !== state.token) return state;
  if ([ACCESS_RECEIVE, ACCESS_FAILURE].includes(action.type) && meta.requestId !== state.requestId) {
    return state;
  }

  switch (action.type) {
    case ACCESS_REQUEST:
      return {
        ...initialState,
        token: meta.token,
        requestId: meta.requestId,
        status: action.error ? 'error' : 'loading',
        error: action.error ? loadError : '',
      };
    case ACCESS_RECEIVE: {
      if (action.error) return { ...state, status: 'error', error: loadError };
      const data = action.payload && (action.payload.data || action.payload);
      if (!data || !['ok', 'active'].includes(data.status)) {
        return { ...state, status: 'error', error: loadError };
      }
      return { ...state, status: 'active', medicines: getMedicines(data.medicines), error: '' };
    }
    case ACCESS_FAILURE:
      return { ...state, status: getFailureOutcome(action.payload) || 'error', error: loadError };
    case CONSENT_REQUEST:
      return {
        ...state,
        submitting: !action.error,
        pendingConsent: action.error ? null : meta.consentStatus,
        error: action.error ? saveError : '',
        status: action.error ? 'save-error' : state.status,
      };
    case CONSENT_RECEIVE:
      return {
        ...state,
        submitting: false,
        pendingConsent: null,
        status: action.error ? 'save-error' : meta.consentStatus === 'provided' ? 'accepted' : 'declined',
        error: action.error ? saveError : '',
      };
    case CONSENT_FAILURE:
      return {
        ...state,
        submitting: false,
        pendingConsent: null,
        status: getFailureOutcome(action.payload) || 'save-error',
        error: saveError,
      };
    default:
      return state;
  }
};
